// Construit un échéancier (rows) à partir de la proposition / contrat liés à une facture.
// Permet d'auto-pré-remplir l'éditeur de traites avec les échéances déjà fixées
// dans la proposition (ou contrat fallback), sans avoir à les ressaisir.

import { supabase } from '@/integrations/supabase/client';
import type { Facture } from '../types';
import { calcTotaux } from '../components/EcheancierEditor';

export type EcheancierRow = { label: string; date?: string; montant: number };

async function resolvePrestationUuid(idOrNumero: string | null | undefined): Promise<string | null> {
  if (!idOrNumero) return null;
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(idOrNumero);
  if (isUuid) return idOrNumero;
  const { data } = await supabase.from('prestations').select('id').eq('numero', idOrNumero).maybeSingle();
  return (data?.id as string) || null;
}

async function fetchPropositionForPrestation(prestUuid: string) {
  const { data: props } = await supabase
    .from('propositions')
    .select('id, numero, mode_paiement, acompte_pct, acompte_jours, nb_echeances, echeancier_overrides, remise_pct')
    .eq('prestation_id', prestUuid)
    .order('created_at', { ascending: false })
    .limit(1);
  let prop: any = props?.[0] || null;

  if (!prop) {
    const { data: ctr } = await supabase
      .from('contrats')
      .select('proposition_id')
      .eq('prestation_id', prestUuid)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (ctr?.proposition_id) {
      const { data: p2 } = await supabase
        .from('propositions')
        .select('id, numero, mode_paiement, acompte_pct, acompte_jours, nb_echeances, echeancier_overrides, remise_pct')
        .eq('id', ctr.proposition_id)
        .maybeSingle();
      if (p2) prop = p2;
    }
  }
  return prop;
}

function buildRowsFromProp(prop: any, ttc: number, dateEvent?: string): EcheancierRow[] {
  if (!prop) return [];
  const overrides: any[] = Array.isArray(prop.echeancier_overrides) ? prop.echeancier_overrides : [];
  if (overrides.length) {
    return overrides.map((o: any, i: number) => ({
      label: o?.label || `Échéance ${i + 1}`,
      date: o?.date || undefined,
      montant: o?.montant != null ? Number(o.montant) : 0,
    }));
  }
  const acomptePct = Math.max(0, Math.min(100, Number(prop.acompte_pct ?? 0)));
  const nbEch = Math.max(1, Math.min(24, Number(prop.nb_echeances ?? 1)));
  const acompteJours = Math.max(0, Number(prop.acompte_jours ?? 0));
  if (!(ttc > 0) || (acomptePct === 0 && nbEch <= 1)) return [];

  const acompte = Math.round(ttc * acomptePct) / 100;
  const solde = Math.max(0, ttc - acompte);
  const refDate = dateEvent ? new Date(dateEvent) : new Date();
  const rows: EcheancierRow[] = [];
  if (acompte > 0) {
    const aDate = new Date();
    aDate.setDate(aDate.getDate() + acompteJours);
    rows.push({ label: `Acompte (${acomptePct}%)`, date: aDate.toISOString().slice(0, 10), montant: acompte });
  }
  let cumul = 0;
  const base = solde / nbEch;
  for (let i = 1; i <= nbEch; i++) {
    const d = new Date(refDate);
    d.setMonth(d.getMonth() + (i - nbEch));
    const isLast = i === nbEch;
    const montant = isLast ? Math.round((solde - cumul) * 100) / 100 : Math.round(base * 100) / 100;
    cumul += montant;
    const label = isLast ? 'Solde' : (nbEch > 1 ? `Traite ${i}/${nbEch - 1}` : 'Solde');
    rows.push({ label, date: d.toISOString().slice(0, 10), montant });
  }
  return rows;
}

// Renvoie l'échéancier dérivé de la propal/contrat (ou [] si rien d'utile).
export async function fetchEcheancierFromProp(facture: Facture, prestDateEvent?: string): Promise<EcheancierRow[]> {
  try {
    const prestUuid = await resolvePrestationUuid(facture.prestationId);
    if (!prestUuid) return [];
    const prop = await fetchPropositionForPrestation(prestUuid);
    if (!prop) return [];
    const { ttc } = calcTotaux(facture);
    return buildRowsFromProp(prop, ttc, prestDateEvent || facture.datePrestation);
  } catch (e) {
    console.warn('[echeancierFromProp] fetch failed', e);
    return [];
  }
}
